import { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { getCompanyFinancialData } from '@/pages/Narrative/utils/calculation';
import {
  calculateFairValue,
  calculateRevenueGrowth,
} from '@/pages/Narrative/utils/genericCalculations';
import {
  getMostRecentReportedYear,
  getPresentRevenue,
} from '@/pages/Narrative/utils/pastAnnualRevenueEarnings';
import {
  FAIR_VALUE_PRECISION,
  NarrativeForm,
} from '@/pages/Narrative/utils/schema';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { currencyToSymbol } from '@/utilities/formatting';
import { useMemo } from 'react';
import {
  convertToListingCurrency,
  convertToReportedCurrency,
  pickCurrencyConversionParamsFromCompanyFinancialData,
} from '@/pages/Narrative/utils/currencyConversion';
import { roundNumberToSignificant } from '@/pages/Narrative/utils/number';
import { PastAnnualRevenueEarnings } from '@/pages/Narrative/types';

type Props = {
  company: NarrativeValuatorCompany;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  values: NarrativeForm;
  setPartialValues: (values: Partial<NarrativeForm>) => void;
};

export const useNarrativeMiniValuatorCalculations = ({
  company,
  pastAnnualRevenueEarnings,
  values,
  setPartialValues,
}: Props) => {
  const companyFinancialData = useMemo(
    () => getCompanyFinancialData(company),
    [company]
  );

  const mostRecentReportedYear = getMostRecentReportedYear(
    pastAnnualRevenueEarnings
  );

  const presentRevenue = getPresentRevenue(
    mostRecentReportedYear,
    companyFinancialData.presentRevenue,
    pastAnnualRevenueEarnings
  );

  const handleOnFairValueChange = (fairValue: number) => {
    const reportedCurrencyFairValue = convertToReportedCurrency({
      ...pickCurrencyConversionParamsFromCompanyFinancialData(
        companyFinancialData
      ),
      exchangeRate: values.exchangeRate,
      fairValue,
    });

    const estimatedFutureRevenuePercentage = calculateRevenueGrowth({
      discountRatePercentage: values.discountRatePercentage,
      fairValue: reportedCurrencyFairValue,
      futurePriceEarningsMultiple: values.priceEarningsMultiple,
      futureProfitMarginPercentage: values.profitMarginPercentage,
      futureSharesPercentage: companyFinancialData.sharesPercentage,
      presentRevenue,
      presentShares: companyFinancialData.presentShares,
      timePeriod: values.timePeriod,
      futureSharesOutstanding: values.futureSharesOutstanding,
    });

    setPartialValues({
      fairValue: roundNumberToSignificant(fairValue, FAIR_VALUE_PRECISION),
      revenuePercentage: estimatedFutureRevenuePercentage,
    });
  };

  const handleOnRevenueChange = (revenuePercentage: number) => {
    const estimatedFairValue = calculateFairValue({
      discountRatePercentage: values.discountRatePercentage,
      futurePriceEarningsMultiple: values.priceEarningsMultiple,
      futureProfitMarginPercentage: values.profitMarginPercentage,
      futureRevenuePercentage: revenuePercentage,
      futureSharesPercentage: companyFinancialData.sharesPercentage,
      presentRevenue,
      presentShares: companyFinancialData.presentShares,
      timePeriod: values.timePeriod,
      futureSharesOutstanding: values.futureSharesOutstanding,
    });
    const listedCurrencyFairValue = convertToListingCurrency({
      ...pickCurrencyConversionParamsFromCompanyFinancialData(
        companyFinancialData
      ),
      exchangeRate: values.exchangeRate,
      fairValue: estimatedFairValue,
    });

    setPartialValues({
      revenuePercentage,
      fairValue: roundNumberToSignificant(
        listedCurrencyFairValue,
        FAIR_VALUE_PRECISION
      ),
    });
  };

  const handleOnTimePeriodChange = (timePeriod: number) => {
    const estimatedFairValue = calculateFairValue({
      discountRatePercentage: values.discountRatePercentage,
      futurePriceEarningsMultiple: values.priceEarningsMultiple,
      futureProfitMarginPercentage: values.profitMarginPercentage,
      futureRevenuePercentage: values.revenuePercentage,
      futureSharesPercentage: companyFinancialData.sharesPercentage,
      presentRevenue,
      presentShares: companyFinancialData.presentShares,
      timePeriod,
      futureSharesOutstanding: values.futureSharesOutstanding,
    });

    const listedCurrencyFairValue = convertToListingCurrency({
      ...pickCurrencyConversionParamsFromCompanyFinancialData(
        companyFinancialData
      ),
      exchangeRate: values.exchangeRate,
      fairValue: estimatedFairValue,
    });

    setPartialValues({
      timePeriod,
      fairValue: roundNumberToSignificant(
        listedCurrencyFairValue,
        FAIR_VALUE_PRECISION
      ),
    });
  };

  const tradingCurrencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? ('XXX' as const);
  const companyCurrencySymbol = currencyToSymbol[tradingCurrencyIso].currency
    .symbol as string;
  const lastSharePrice = company.analysisValue?.lastSharePrice ?? null;

  return {
    companyCurrencySymbol,
    tradingCurrencyIso,
    lastSharePrice,
    pastAnnualRevenueEarnings,
    companyFinancialData,
    handleOnFairValueChange,
    handleOnRevenueChange,
    handleOnTimePeriodChange,
  };
};
